import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  BookOpen, 
  CheckSquare,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema
} from '../utils/schemaGenerator';

const ResourcesHubPage = () => {
  const guides = [
    {
      title: "Deep Cleaning Guide",
      description: "A comprehensive room-by-room guide to deep cleaning your home, with insights from Heather's 15+ years of executive housekeeping experience.",
      icon: Sparkles,
      link: "/resources/deep-cleaning-guide",
      topics: ["When to deep clean", "Room-by-room checklists", "Brevard-specific challenges", "DIY vs Professional"]
    }
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Cleaning Resources & Expert Guides | Pierce Premier",
      description: "Expert cleaning guides and resources from Pierce Premier. Learn from Heather's 15+ years of executive housekeeping experience. Brevard County cleaning tips.",
      url: "/resources"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Resources" }
    ]),
    generateLocalBusinessSchema()
  );

  return (
    <>
      <SEO 
        title="Cleaning Resources & Expert Guides | Pierce Premier | Brevard County"
        description="Expert cleaning guides and resources from Pierce Premier. Learn from 15+ years of executive housekeeping experience. Brevard County-specific cleaning tips and advice."
        canonical="/resources"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-5xl text-center">
            <Badge className="bg-emerald-600 text-white mb-4">Expert Insights</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Cleaning Resources
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Drawing from Heather's 15+ years of executive housekeeping experience, these guides offer practical advice, detailed checklists, and insights tailored to Brevard County homes. Whether you're tackling a project yourself or considering professional help, these resources will help you understand what quality cleaning really means.
            </p>
          </div>
        </section>

        {/* Guides Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Expert Cleaning Guides
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                In-depth, evergreen content that demonstrates our expertise and helps you make informed decisions about your home.
              </p>
            </div>

            <div className="grid md:grid-cols-1 gap-8">
              {guides.map((guide, idx) => {
                const Icon = guide.icon;
                return (
                  <Card key={idx} className="border-2 hover:border-emerald-200 transition-all hover:shadow-xl">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">{guide.title}</h3>
                          <p className="text-gray-600 mb-6 leading-relaxed">{guide.description}</p>
                          <div className="mb-6">
                            <h4 className="font-semibold text-gray-900 mb-3">What's covered:</h4>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {guide.topics.map((topic, topicIdx) => (
                                <div key={topicIdx} className="flex items-center gap-2 text-gray-700">
                                  <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                  <span className="text-sm">{topic}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <Link 
                            to={guide.link}
                            className="inline-flex items-center text-emerald-600 font-semibold hover:text-emerald-700"
                          >
                            Read Full Guide
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why We Share Resources */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <BookOpen className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Share This Expertise
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  At Pierce Premier, we believe in transparency and education. These resources aren't just marketing—they're Heather's real-world knowledge from over 15 years in executive housekeeping and Bob and Heather's experience running a cleaning business in Brevard County.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you decide to hire us or tackle a project yourself, we want you to understand what quality cleaning looks like and how to approach it the right way.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Have Questions?
            </h2>
            <p className="text-xl mb-8 text-emerald-50">
              Our resources are a starting point. If you'd like to discuss your specific cleaning needs, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow hover:bg-primary/90 h-11 px-8 bg-white text-emerald-600 hover:bg-gray-100 text-lg py-6">
                  Contact Us
                </button>
              </Link>
              <a href="tel:+1-321-408-1347">
                <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow-sm h-11 px-8 border-2 border-white text-white hover:bg-white hover:text-emerald-600 text-lg py-6">
                  321-408-1347
                </button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ResourcesHubPage;